/**
 * A flow definition, laid out so a person can read its branches (#1736).
 *
 * ── what this is for ────────────────────────────────────────────────────────
 * The console's Flows page reads `core.describe_flow` today and uses it for one
 * thing: which manual edges leave the instance's CURRENT stage. The definition
 * it already holds — every stage, every transition, the gates, timers,
 * subflows, conditions and action lists — is never drawn. #1736 asks for a
 * *"calm branch-aware route map: stages are stations, transitions are lines,
 * branches stay legible rather than becoming a force graph"*, and for an
 * *"outline/list and keyboard mode [that] is equally complete; the map is not
 * the only way to author"*.
 *
 * This module is the layout half, and it is deliberately pure: no DOM, no
 * React, no measurement. That is what lets the map and the outline be two
 * renderings of ONE derivation rather than two implementations that agree until
 * they do not.
 *
 * ── the layering rule, and why not a force graph ───────────────────────────
 * Stations are placed by LONGEST path from an entry stage. Longest rather than
 * shortest, because with shortest-path layering a stage reachable both directly
 * and via a detour lands beside its own predecessor and the edge draws
 * backwards for no reason a reader could name. Longest-path guarantees every
 * forward edge points forward.
 *
 * A flow is not a DAG — `2026081002` supports backwards edges, and a rework
 * loop ("returned" → "in progress") is an ordinary business fact. The edges that
 * CLOSE a cycle are found first, by depth-first search, and then kept out of the
 * walk entirely; the walk runs over what is left, which is a DAG.
 *
 * That order matters, and the first version of this file got it wrong. It
 * excluded only edges pointing AT an entry stage, on the assumption that a
 * rework loop returns to the start. Most do not: `controlplane`'s provisioning
 * flow loops `blocked → running`, and `running` is not the initial state
 * (`2026082203:136-141`). Such a loop fed the relaxation, spun for the full
 * pass bound, and pushed its members into the rightmost columns — after which
 * every genuine forward edge out of them satisfied `to ≤ from` and was drawn as
 * a RETURN, while the real return was drawn as a step forward. A five-stage
 * flow rendered as twelve columns with every arrow reversed. A self-loop
 * (`core.status_transition` has no `from <> to` check, `0046:39-48`) did the
 * same thing.
 *
 * ── what it does not do ────────────────────────────────────────────────────
 * No pixels. Callers decide how wide a station is and how a line is drawn. This
 * says only: which column each stage belongs in, in what order within it, which
 * edges go forward, which return, and which stages nothing reaches.
 */

/** A stage as `core.describe_flow` returns it. Structural, so a caller may pass more. */
export interface FlowMapStage {
  code: string
  kind?: string | null
  gate?: unknown
  timers?: unknown
  sub_flow?: string | null
  advance_action?: string | null
}

/** A transition as `core.describe_flow` returns it. */
export interface FlowMapTransition {
  from: string
  to: string
  trigger?: string | null
  condition?: unknown
  actions?: unknown
  priority?: number | null
}

/** One placed station. */
export interface FlowMapNode {
  code: string
  /** Column index: 0 is an entry stage. */
  column: number
  /** Position within the column, stable across renders. */
  row: number
  /** Nothing in the definition reaches this stage. */
  unreachable: boolean
}

/** One drawn line. */
export interface FlowMapEdge {
  from: string
  to: string
  /** The target sits at or before the source — a rework loop, not a step on. */
  back: boolean
  /** Both ends are placed. A dangling edge is reported rather than drawn. */
  dangling: boolean
  trigger?: string | null
  priority?: number | null
}

export interface FlowMap {
  nodes: FlowMapNode[]
  edges: FlowMapEdge[]
  /** Column count, so a caller can size a grid without walking the nodes. */
  columns: number
  /** Edges naming a stage the definition does not declare. */
  dangling: FlowMapEdge[]
}

/**
 * Where the flow STARTS.
 *
 * The caller passes it, because the platform already knows: a flow runs on a
 * `core.status_machine`, and that machine declares `is_initial`.
 * `core.describe_flow` embeds the machine, so the console has the answer
 * without this module guessing.
 *
 * Guessing is the thing to avoid. "No incoming edge" describes an entry stage
 * AND an orphan left behind by an edit, and a map that cannot tell them apart
 * silently reports a broken definition as a second starting point — on the one
 * surface built to show definitions. With a declared entry, every other
 * in-degree-0 stage is what it looks like: unreachable.
 *
 * Without one, the heuristic is used and nothing is called unreachable, because
 * there is no evidence to call it that. When every stage is reachable — which a
 * pure loop is — the first in declaration order anchors the walk, so a cyclic
 * definition still draws rather than producing a blank page.
 */
function entryStages(
  stages: readonly FlowMapStage[], edges: readonly FlowMapTransition[], entry?: string | null,
): string[] {
  if (entry && stages.some((s) => s.code === entry)) return [entry]
  const reached = new Set(edges.map((e) => e.to))
  const entries = stages.filter((s) => !reached.has(s.code)).map((s) => s.code)
  if (entries.length > 0) return entries
  return stages.length > 0 ? [stages[0]!.code] : []
}

/**
 * Which transitions CLOSE a cycle — the returns, found before anything is placed.
 *
 * Depth-first search from the entry stages, then from anything they did not
 * reach (in declaration order, so an orphaned component is still walked). An
 * edge whose target is on the current stack closes a cycle; a self-loop is that
 * same case with a stack of one. An edge to a stage already FINISHED is a
 * forward or cross edge, not a return — the distinction the shape-based test
 * `to ≤ from` could not make.
 *
 * Indices into the caller's array, not codes: `core.flow_transition`'s key is
 * `(flow_id, from_code, to_code)`, so a pair cannot repeat within one flow, but
 * this module takes whatever a caller hands it and must not merge two rows that
 * happen to name the same pair.
 *
 * Iterative rather than recursive: the stack is the algorithm here, and making
 * it explicit is what bounds it by the definition's size instead of the
 * engine's.
 */
function returnEdges(
  stages: readonly FlowMapStage[], edges: readonly FlowMapTransition[], entries: readonly string[],
): Set<number> {
  const declared = new Set(stages.map((s) => s.code))
  const outgoing = new Map<string, number[]>()
  edges.forEach((e, i) => {
    if (!declared.has(e.from) || !declared.has(e.to)) return
    const at = outgoing.get(e.from)
    if (at) at.push(i)
    else outgoing.set(e.from, [i])
  })

  const returns = new Set<number>()
  const onStack = new Set<string>()
  const finished = new Set<string>()

  for (const root of [...entries, ...stages.map((s) => s.code)]) {
    if (!declared.has(root) || finished.has(root)) continue
    const stack: { code: string; next: number }[] = [{ code: root, next: 0 }]
    onStack.add(root)
    while (stack.length > 0) {
      const top = stack[stack.length - 1]!
      const out = outgoing.get(top.code) ?? []
      if (top.next >= out.length) {
        onStack.delete(top.code)
        finished.add(top.code)
        stack.pop()
        continue
      }
      const index = out[top.next]!
      top.next += 1
      const to = edges[index]!.to
      if (onStack.has(to)) { returns.add(index); continue }
      if (finished.has(to)) continue
      onStack.add(to)
      stack.push({ code: to, next: 0 })
    }
  }
  return returns
}

/**
 * Longest-path column for every stage, over the definition MINUS its returns.
 *
 * Iterative relaxation bounded by the stage count: an edge can only ever push a
 * target one column further than its source, so after N passes nothing can move
 * again. What is walked is a DAG, so the bound is a guard rather than the
 * termination argument — the returns were removed before this ran.
 */
function columnsOf(
  stages: readonly FlowMapStage[], edges: readonly FlowMapTransition[], returns: ReadonlySet<number>,
): Map<string, number> {
  const declared = new Set(stages.map((s) => s.code))
  const column = new Map<string, number>()
  for (const s of stages) column.set(s.code, 0)

  for (let pass = 0; pass < stages.length; pass += 1) {
    let moved = false
    edges.forEach((e, i) => {
      if (returns.has(i)) return
      if (!declared.has(e.from) || !declared.has(e.to)) return
      const next = (column.get(e.from) ?? 0) + 1
      if (next > (column.get(e.to) ?? 0)) { column.set(e.to, next); moved = true }
    })
    if (!moved) break
  }
  return column
}

/**
 * The definition, placed.
 *
 * Order within a column follows DECLARATION order, not the code's alphabet: the
 * author's sequence is information, and re-sorting it would silently reorder a
 * branch somebody arranged deliberately.
 */
export function flowMap(
  stages: readonly FlowMapStage[], transitions: readonly FlowMapTransition[],
  entry?: string | null,
): FlowMap {
  const declared = new Set(stages.map((s) => s.code))
  const entryList = entryStages(stages, transitions, entry)
  const returns = returnEdges(stages, transitions, entryList)
  const column = columnsOf(stages, transitions, returns)
  const reached = new Set(transitions.filter((t) => declared.has(t.from)).map((t) => t.to))
  const entries = new Set(entryList)
  // Only a DECLARED entry makes "unreachable" a claim worth printing; without
  // one, every in-degree-0 stage is a candidate entry and none is an orphan.
  const known = Boolean(entry && declared.has(entry))

  const rowOf = new Map<number, number>()
  const nodes: FlowMapNode[] = stages.map((s) => {
    const col = column.get(s.code) ?? 0
    const row = rowOf.get(col) ?? 0
    rowOf.set(col, row + 1)
    return {
      code: s.code, column: col, row,
      // An entry stage is not "unreachable" — it is where you start.
      unreachable: known && !reached.has(s.code) && !entries.has(s.code),
    }
  })

  const edges: FlowMapEdge[] = transitions.map((t, i) => ({
    from: t.from, to: t.to,
    dangling: !declared.has(t.from) || !declared.has(t.to),
    // An edge with an undeclared end is never in `returns` — the search only
    // walks declared stages — so a broken edge is reported as broken and not
    // also as a rework loop.
    back: returns.has(i),
    trigger: t.trigger ?? null,
    priority: t.priority ?? null,
  }))

  return {
    nodes,
    edges,
    columns: nodes.reduce((max, n) => Math.max(max, n.column + 1), 0),
    dangling: edges.filter((e) => e.dangling),
  }
}
